import { NavigationFailureType, isNavigationFailure } from 'vue-router'
import { App } from '@capacitor/app'

import bus from '@/plugins/eventbus'
import toast from '@/plugins/toasts'
import { stateGetters, stateSetters } from '@/utils/store/store-utils'
import router from '@/plugins/router'
import { classifyShare } from '@/utils/share-classify'
import { ADD_ROUTES, ADD_QUERY, ResourceType } from '@/utils/constants'
import { setAppActive } from '@/utils/app-state'

// Share intents land on the add screen with the bookmark<->note toggle shown
// (and, on mobile, no auto-create) so the user can choose the type.
const addRouteWithTypeChange = type => ({
  path: ADD_ROUTES[type],
  query: { [ADD_QUERY.ALLOW_TYPE_CHANGE]: 'true' },
})

const defaultState = {
  intent: null,
  backButtonHandler: null,
}

export default {
  name: 'native',
  namespaced: true,
  state: () => defaultState,
  getters: {
    ...stateGetters(defaultState),
  },
  mutations: {
    ...stateSetters(defaultState),
  },
  actions: {
    clearBackButtonHandler({ getters, commit }) {
      getters.backButtonHandler?.destroy()
      commit('setBackButtonHandler', null)
    },
    setupBackButtonListener({ commit, dispatch }) {
      dispatch('clearBackButtonHandler')

      // TODO make abstraction layer over this and let listeners subscribe to it
      // (because there's no way to check if there are other listeners here)
      // ionic/vue has a priority system, might be an option but maybe overkill
      const handler = App.addListener('backButton', event => {
        console.log('Native back button detected')
        dispatch('onBackButtonPressed', event)
      })

      commit('setBackButtonHandler', handler)
    },
    onBackButtonPressed({ getters, commit }, event) {
      // default behaviour
      if (event.canGoBack) {
        router.back()
        return
      }

      // close via active intent
      if (getters.intent) {
        console.log('Exiting via intent')

        getters.intent.finish()
        commit('setIntent', null)
        return
      }

      App.exitApp()
    },
    //
    onNewIntent({ commit }, intent) {
      // Doesn't need to do anything else for now
      commit('setIntent', intent)
    },
  },
}

// Track recently-handled share URLs to dedupe duplicate appUrlOpen deliveries.
// AppDelegate calls ApplicationDelegateProxy.shared.application(...) twice
// (see ios/App/App/AppDelegate.swift:45-47), which can emit appUrlOpen twice
// for the same URL. Without dedup, the form would auto-submit twice.
const recentlyHandledShareUrls = new Set()

// The iOS share extension double-encodes query values: it pre-applies
// addingPercentEncoding(.urlHostAllowed) on the value BEFORE URLComponents
// re-encodes for the query. URL.searchParams.get decodes only once, so
// non-ASCII text comes back still URL-encoded (e.g. "Kérdés" → "K%C3%A9rd%C3%A9s").
// Decode again, falling back if the result has invalid percent-encoding.
function decodeShareValue(raw) {
  if (!raw) return ''
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

// Detect rubra://?title=...&type=...&url=... URLs from the iOS share extension
// (see ios/App/rubra share extension/ActionViewController.swift sendData()).
// Returns true if the URL was a share intent and was handled.
function handleShareIntentUrl(app, url) {
  if (!url.searchParams.has('title') && !url.searchParams.has('url')) return false

  if (recentlyHandledShareUrls.has(url.href)) {
    console.log('Duplicate share URL, skipping', url.href)
    return true
  }
  recentlyHandledShareUrls.add(url.href)
  setTimeout(() => recentlyHandledShareUrls.delete(url.href), 2000)

  const title = decodeShareValue(url.searchParams.get('title')).trim()
  const sharedUrl = decodeShareValue(url.searchParams.get('url')).trim()
  const type = decodeShareValue(url.searchParams.get('type'))
  // Set by the iOS share extension when it detects a Safari text selection
  // (selection text + page URL/title). See ActionViewController.swift.
  const sourceUrl = decodeShareValue(url.searchParams.get('sourceUrl')).trim()
  const sourceTitle = decodeShareValue(url.searchParams.get('sourceTitle')).trim()
  if (!title && !sharedUrl) return false

  // PDF data share from iOS Safari's QuickLook viewer arrives as
  // type=application/pdf with a file:// URL pointing into the app-group container
  // (ActionViewController.swift handleTypePdf). The backend can't fetch file:// URLs,
  // so show a clear message instead of falling through to a useless note.
  if (type === 'application/pdf' && sharedUrl && sharedUrl.startsWith('file://')) {
    toast.error("Can't bookmark PDFs directly yet. Share the page URL from Safari's address bar instead.")
    return true
  }

  let decision
  if (sourceUrl) {
    // Safari text selection: the selected text is in `title`, source is explicit.
    decision = classifyShare({ text: title, explicitSource: { url: sourceUrl, title: sourceTitle || null } })
  } else if (sharedUrl && /^https?:\/\//i.test(sharedUrl)) {
    // URL share (handleTypeUrl): page title carried in the title param.
    decision = classifyShare({ text: sharedUrl, pageTitle: title || null })
  } else {
    // Text share (handleTypeText puts selection / app-entry text in the title param).
    decision = classifyShare({ text: title })
  }

  const finish = () => {
    app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
  }

  if (decision.kind === 'bookmark') {
    app.config.globalProperties.$store.dispatch('native/onNewIntent', {
      url: decision.url,
      result: { url: decision.url, title: decision.title ?? undefined, type },
      finish,
    })
    router.replace(addRouteWithTypeChange(ResourceType.BOOKMARK)).catch(() => {})
    return true
  }

  app.config.globalProperties.$store.dispatch('native/onNewIntent', {
    type: 'note',
    title: decision.title,
    contentHtml: decision.contentHtml,
    result: { title: decision.title, contentHtml: decision.contentHtml, type: 'text/plain' },
    finish,
  })
  router.replace(addRouteWithTypeChange(ResourceType.NOTE)).catch(() => {})
  return true
}

// Use mitt's 'on' method instead of Vue 2's $once
bus.on('app:created', async app => {
  if (app.config.globalProperties.$isNative) {
    app.config.globalProperties.$store.dispatch('native/setupBackButtonListener')

    // Track foreground/background for connectivity-aware error handling (app-state.js).
    App.addListener('appStateChange', ({ isActive }) => setAppActive(isActive))

    // handle deeplinks
    // NOTE this has buggy behaviour when running with `yarn dev:android`
    // probably because the app is never really killed, building app via Android Studio shows normal behaviour
    App.addListener('appUrlOpen', event => {
      console.log('App was opened from external url', event.url)
      let url
      try {
        url = new URL(event.url) // this also works with app url schemes, ex. com.rubra.appclient
      } catch (err) {
        console.warn('Could not parse appUrlOpen URL', event.url, err)
        return
      }

      // iOS share-extension delivers shared content via rubra:// URL scheme with
      // title/description/type/url query params. Handle this directly instead of
      // relying on the SendIntent plugin's notification → bridge.eval → window-event
      // chain, which is unreliable on iOS 26+ when the app is already running.
      if (handleShareIntentUrl(app, url)) return

      if (!url.pathname) {
        console.warn('No pathname found in url, ignoring...')
        return
      }

      router.push(url.pathname + url.search).catch(err => {
        // Only duplicated navigations
        if (isNavigationFailure(err, NavigationFailureType.duplicated)) {
          // Use mitt's emit method instead of Vue 2's $emit
          bus.emit('router:error:duplicated')
        } else {
          console.error(err)
        }
      })
    })

    // Cold-launch share: if the app was just launched by tapping Rubra in the
    // share sheet, the URL won't fire appUrlOpen (the listener registers above).
    // App.getLaunchUrl() returns it explicitly.
    try {
      const launchUrlResult = await App.getLaunchUrl()
      if (launchUrlResult?.url) {
        handleShareIntentUrl(app, new URL(launchUrlResult.url))
      }
    } catch (err) {
      console.warn('Could not handle launch URL', err)
    }
  }
})
